/**
 * CrawlWaitingContent — animated "while you wait" content shown during
 * website/bio crawling.  Displays a scanning visual with rotating tips
 * about Kindredly so the user stays engaged during the 15-30s wait.
 *
 * This is the **inner content** extracted so it can be reused in:
 *   - CrawlWaitingOverlay (admin setup wizard — wraps in a Dialog)
 *   - ParsingStep (provider onboarding — rendered inline in existing Dialog)
 *
 * Uses MUI theme palette (primary.main, secondary.main) instead of
 * hardcoded hex values so colors stay in sync with the design system.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';

/** Kindredly feature tips shown on a rotating carousel. */
const KINDREDLY_TIPS = [
  {
    icon: '\u{1F4DD}',
    title: 'AI-Powered Notes',
    body: 'Kindredly writes your SOAP notes, risk assessments, and treatment plans in real time \u2014 so you can focus on your client.',
  },
  {
    icon: '\u{1F4C5}',
    title: 'Smart Scheduling',
    body: 'Clients can self-book appointments, and your calendar syncs with Google Calendar automatically.',
  },
  {
    icon: '\u{1F3A4}',
    title: 'Drive Mode',
    body: 'Dictate notes naturally while on the road and let the AI structure them for review when you are back at your desk.',
  },
  {
    icon: '\u{1F4CA}',
    title: 'Practice Analytics',
    body: 'Track revenue, session counts, and client retention at a glance with your AI Practice Advisor.',
  },
  {
    icon: '\u{1F4B3}',
    title: 'Built-in Billing',
    body: 'Generate superbills, manage invoices, and submit insurance claims \u2014 all from one place.',
  },
  {
    icon: '\u{1F46A}',
    title: 'Client Portal',
    body: 'Your clients get their own portal for appointments, paperwork, health tracking, and secure messaging.',
  },
  {
    icon: '\u{1F512}',
    title: 'HIPAA Compliant',
    body: 'All data is encrypted at rest and in transit. Your clients\u2019 information is always protected.',
  },
  {
    icon: '\u{1F9E0}',
    title: 'Personalized AI',
    body: 'The AI learns your writing style, preferred note templates, and clinical approach over time.',
  },
];

/** Playful status messages that rotate while scanning. */
const DEFAULT_SCANNING_PHASES = [
  'Reading your homepage\u2026',
  'Finding your team members\u2026',
  'Discovering your specialties\u2026',
  'Checking for appointment types\u2026',
  'Looking for your logo and brand colors\u2026',
  'Almost there \u2014 organizing everything\u2026',
];

/** Provider-specific status messages for bio crawl. */
const PROVIDER_SCANNING_PHASES = [
  'Reading your bio page\u2026',
  'Identifying your specialties\u2026',
  'Finding your credentials\u2026',
  'Drafting your booking-page bio\u2026',
  'Setting up your preferences\u2026',
  'Almost there \u2014 organizing everything\u2026',
];

/** How long (ms) before showing the safety "Close" button if crawl hangs. */
const SAFETY_CLOSE_DELAY_MS = 60_000;

export interface CrawlWaitingContentProps {
  /** Whether the content is actively visible/running. */
  active: boolean;
  /** Title shown above the scanning phases. Defaults to "Scanning your website". */
  title?: string;
  /** Custom scanning phase messages. Defaults to website crawl phases. */
  scanningPhases?: string[];
  /** Optional callback for a safety close button (shown after 60s). */
  onSafetyClose?: () => void;
}

export default function CrawlWaitingContent({
  active,
  title = 'Scanning your website',
  scanningPhases = DEFAULT_SCANNING_PHASES,
  onSafetyClose,
}: CrawlWaitingContentProps) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const secondaryColor = theme.palette.secondary.main;

  const [tipIndex, setTipIndex] = useState(0);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [showSafetyClose, setShowSafetyClose] = useState(false);

  // Rotate tips every 4.5 seconds with a fade transition
  useEffect(() => {
    if (!active) return;
    let fadeTimeout: ReturnType<typeof setTimeout>;
    const interval = setInterval(() => {
      setFadeIn(false);
      fadeTimeout = setTimeout(() => {
        setTipIndex((prev) => (prev + 1) % KINDREDLY_TIPS.length);
        setFadeIn(true);
      }, 300);
    }, 4500);
    return () => {
      clearInterval(interval);
      clearTimeout(fadeTimeout);
    };
  }, [active]);

  // Rotate scanning phases every 3.5 seconds
  useEffect(() => {
    if (!active) return;
    const interval = setInterval(() => {
      setPhaseIndex((prev) => (prev + 1) % scanningPhases.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [active, scanningPhases]);

  // Reset state when content becomes active
  useEffect(() => {
    if (active) {
      setTipIndex(0);
      setPhaseIndex(0);
      setFadeIn(true);
      setShowSafetyClose(false);
    }
  }, [active]);

  // Show a safety close button if the crawl takes too long
  useEffect(() => {
    if (!active) return;
    const timeout = setTimeout(() => setShowSafetyClose(true), SAFETY_CLOSE_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [active]);

  const handleSafetyClose = useCallback(() => {
    if (onSafetyClose) onSafetyClose();
  }, [onSafetyClose]);

  const tip = KINDREDLY_TIPS[tipIndex];

  return (
    <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Animated scanning visual */}
      <Box
        sx={{
          width: { xs: 64, sm: 80 },
          height: { xs: 64, sm: 80 },
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2.5,
          position: 'relative',
          animation: 'crawl-pulse 2s ease-in-out infinite',
          '@keyframes crawl-pulse': {
            '0%, 100%': {
              transform: 'scale(1)',
              boxShadow: `0 0 0 0 ${primaryColor}4D`,
            },
            '50%': {
              transform: 'scale(1.06)',
              boxShadow: `0 0 0 16px ${primaryColor}00`,
            },
          },
        }}
      >
        {/* Orbiting dot */}
        <Box
          sx={{
            position: 'absolute',
            width: { xs: 10, sm: 12 },
            height: { xs: 10, sm: 12 },
            borderRadius: '50%',
            bgcolor: 'secondary.main',
            animation: 'crawl-orbit 2.5s linear infinite',
            '@keyframes crawl-orbit': {
              '0%': { transform: 'rotate(0deg) translateX(48px) rotate(0deg)' },
              '100%': { transform: 'rotate(360deg) translateX(48px) rotate(-360deg)' },
            },
          }}
        />
        <Typography
          sx={{
            fontSize: { xs: 28, sm: 36 },
            lineHeight: 1,
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
          }}
        >
          {'\u{1F310}'}
        </Typography>
      </Box>

      {/* Scanning status */}
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 2,
          minHeight: 24,
          transition: 'opacity 0.3s ease',
        }}
      >
        {scanningPhases[phaseIndex]}
      </Typography>

      {/* Progress bar */}
      <LinearProgress
        sx={{
          mb: 3,
          borderRadius: 2,
          height: 6,
          bgcolor: 'action.hover',
          '& .MuiLinearProgress-bar': {
            background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
            borderRadius: 2,
          },
        }}
      />

      {/* Tip carousel */}
      <Box
        sx={{
          bgcolor: 'action.hover',
          borderRadius: 2,
          px: { xs: 1.5, sm: 2.5 },
          py: 2,
          minHeight: { xs: 100, sm: 120 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: fadeIn ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      >
        <Typography
          variant="overline"
          color="primary"
          sx={{
            fontWeight: 700,
            letterSpacing: 1.2,
            mb: 1,
            fontSize: { xs: '0.65rem', sm: '0.75rem' },
          }}
        >
          While you wait, did you know?
        </Typography>
        <Typography sx={{ fontSize: { xs: 24, sm: 28 }, lineHeight: 1, mb: 0.75 }}>
          {tip.icon}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
        >
          {tip.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 280, fontSize: { xs: '0.78rem', sm: '0.875rem' } }}
        >
          {tip.body}
        </Typography>
      </Box>

      {/* Dot indicators */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: 1.5 }}>
        {KINDREDLY_TIPS.map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: i === tipIndex ? 'primary.main' : 'action.disabled',
              transition: 'background-color 0.3s ease',
            }}
          />
        ))}
      </Box>

      {/* Safety close button -- appears after 60s if crawl hangs */}
      {showSafetyClose && onSafetyClose && (
        <Button
          variant="text"
          size="small"
          onClick={handleSafetyClose}
          sx={{ mt: 2, color: 'text.secondary', textTransform: 'none' }}
        >
          Taking too long? Close and try again
        </Button>
      )}
    </Box>
  );
}

export { PROVIDER_SCANNING_PHASES };
